/*
 * Copyright © 2020-2025 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 表格列定义
 * @LastEditors: yingongjing
 * @LastEditTime: 2025-12-12 09:36:49
 */

import React from 'react'
import { Button, Tag, Tooltip, Input } from 'antd'
import { TestDataItem } from '../hooks/useCSVUpload'
import { WatermarkExtractionStatus } from '../../context/WatermarkBatchContext'
import {
  removeOssResizeParam,
  addOssResizeParam,
  parseDiffData,
  getExtractionStatusDisplay
} from '../utils/watermarkUtils'
import { WatermarkApplyStatus } from '../../hooks/useWatermarkApply'
import './TableColumns.scss'
import { ReloadOutlined } from '@ant-design/icons'

const { TextArea } = Input

interface TableColumnsProps {
  currentPage: number
  pageSize: number
  setHoveredRecord: (record: TestDataItem | null) => void
  handleReapplySingle: (record: TestDataItem) => void
  updateDataSource: (updater: (prev: TestDataItem[]) => TestDataItem[]) => void
}

export const createTableColumns = ({
  currentPage,
  pageSize,
  setHoveredRecord,
  handleReapplySingle,
  updateDataSource
}: TableColumnsProps) => {
  return [
    {
      title: 'ID',
      key: 'id',
      width: 50,
      render: (_: any, record: TestDataItem, index: number) => {
        // 使用当前页的索引计算实际ID
        const startIndex = (currentPage - 1) * pageSize
        return startIndex + index + 1
      }
    },
    {
      title: '原图',
      dataIndex: 'oldUrl',
      key: 'oldUrl',
      width: 150,
      render: (text: string, record: TestDataItem) =>
        text ? (
          <img
            src={addOssResizeParam(text, 200)}
            alt="原图"
            style={{
              maxWidth: '150px',
              maxHeight: '84px',
              height: 'auto',
              width: 'auto',
              objectFit: 'contain',
              cursor: 'pointer'
            }}
            onMouseEnter={() => {
              setHoveredRecord(record)
            }}
            onError={(e) => {
              ;(e.target as HTMLImageElement).style.display = 'none'
            }}
          />
        ) : (
          <span>-</span>
        )
    },
    {
      title: '手动编辑',
      dataIndex: 'newUrl',
      key: 'newUrl',
      width: 150,
      render: (text: string, record: TestDataItem) =>
        text ? (
          <img
            src={addOssResizeParam(text, 200)}
            alt="手动编辑"
            style={{
              maxWidth: '150px',
              maxHeight: '84px',
              height: 'auto',
              width: 'auto',
              objectFit: 'contain',
              cursor: 'pointer'
            }}
            onMouseEnter={() => {
              setHoveredRecord(record)
            }}
            onError={(e) => {
              ;(e.target as HTMLImageElement).style.display = 'none'
            }}
          />
        ) : (
          <span>-</span>
        )
    },
    {
      title: '自动处理',
      dataIndex: 'autoUrl',
      key: 'autoUrl',
      width: 150,
      render: (text: string, record: TestDataItem) => {
        const applyStatus = record.applyStatus
        const applyShowReason = record.applyShowReason

        // 如果有自动处理的链接，显示图片
        if (text) {
          return (
            <img
              src={addOssResizeParam(text, 200)}
              alt="自动处理"
              style={{
                maxWidth: '150px',
                maxHeight: '84px',
                height: 'auto',
                width: 'auto',
                objectFit: 'contain',
                cursor: 'pointer'
              }}
              onMouseEnter={() => {
                setHoveredRecord(record)
              }}
              onError={(e) => {
                ;(e.target as HTMLImageElement).style.display = 'none'
              }}
            />
          )
        }

        // 如果应用状态不等于1，显示状态和错误原因
        if (applyStatus !== undefined && applyStatus !== 1) {
          const statusText =
            applyStatus === 0
              ? '失败'
              : applyStatus === 2
                ? '无修改项'
                : applyStatus === 3
                  ? '超时'
                  : `状态: ${applyStatus}`
          return (
            <Tooltip title={applyShowReason || statusText} placement="top">
              <div style={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>
                <Tag
                  color={
                    applyStatus === 0
                      ? 'red'
                      : applyStatus === 3
                        ? 'yellow'
                        : '#666666'
                  }
                >
                  {statusText}
                </Tag>
                {applyShowReason && (
                  <div
                    style={{ marginTop: 4, fontSize: '12px', color: '#999' }}
                  >
                    {applyShowReason}
                  </div>
                )}
              </div>
            </Tooltip>
          )
        }

        // 默认显示 "-"
        return <span>-</span>
      }
    },
    {
      title: '处理状态',
      dataIndex: 'extractionStatus',
      key: 'extractionStatus',
      width: 120,
      render: (
        status: WatermarkExtractionStatus | undefined,
        record: TestDataItem
      ) => {
        // 优先显示应用状态
        if (record.applyStatus !== undefined) {
          let applyText = ''
          let applyColor = ''
          let tooltipText = ''

          switch (record.applyStatus) {
            case WatermarkApplyStatus.EDITED:
              applyText = '处理完成'
              applyColor = 'green'
              break
            case WatermarkApplyStatus.FAILED:
              applyText = '应用失败'
              applyColor = 'red'
              tooltipText = record.applyShowReason || '应用失败'
              break
            case WatermarkApplyStatus.NO_CHANGES:
              applyText = '无修改项'
              applyColor = 'orange'
              tooltipText = record.applyShowReason || '无修改项'
              break
            case WatermarkApplyStatus.TIMEOUT:
              applyText = '应用超时'
              applyColor = 'yellow'
              tooltipText = record.applyShowReason || '应用超时'
              break
            default:
              applyText = `状态: ${record.applyStatus}`
              applyColor = 'gray'
              tooltipText = record.applyShowReason || ''
          }

          const tag = (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                minHeight: '60px'
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  flexDirection: 'column',
                  width: '100%'
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    width: '100%'
                  }}
                >
                  <Tag color={applyColor} style={{ margin: 0 }}>
                    {applyText}
                  </Tag>
                </div>
                {/* 如果有自动处理的链接，显示刷新按钮 */}
                {record.autoUrl && (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      width: '100%'
                    }}
                  >
                    <Button
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleReapplySingle(record)
                      }}
                    >
                      重新应用
                    </Button>
                  </div>
                )}
              </div>
              {record.applyDuration !== undefined && (
                <div
                  style={{
                    fontSize: '12px',
                    color: '#999',
                    marginTop: '4px',
                    textAlign: 'center',
                    width: '100%'
                  }}
                >
                  请求响应时长：{record.applyDuration}ms
                </div>
              )}
            </div>
          )

          // 如果有错误信息，显示 Tooltip
          if (tooltipText) {
            return (
              <Tooltip title={tooltipText} placement="top">
                {tag}
              </Tooltip>
            )
          }

          return tag
        }

        // 如果没有应用状态，显示提取状态
        const { text, color } = getExtractionStatusDisplay(status)
        const tagContent = (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Tag color={color}>{text}</Tag>
            {/* 如果有自动处理的链接，显示刷新按钮 */}
            {record.autoUrl && (
              <Button
                type="primary"
                size="small"
                onClick={(e) => {
                  e.stopPropagation()
                  handleReapplySingle(record)
                }}
                style={{
                  minWidth: 'auto',
                  padding: '2px 6px'
                }}
                title="重新应用"
              >
                <ReloadOutlined /> 刷新
              </Button>
            )}
          </div>
        )

        // 如果状态为失败或超时，且有错误信息，则显示 Tooltip
        if (
          (status === WatermarkExtractionStatus.FAILED ||
            status === WatermarkExtractionStatus.EXTRACTING_TIMEOUT) &&
          record.errorMsg
        ) {
          return (
            <Tooltip title={record.errorMsg} placement="top">
              {tagContent}
            </Tooltip>
          )
        }

        return tagContent
      }
    },
    {
      title: '修改内容',
      dataIndex: 'diff',
      key: 'diffContent',
      width: 400,
      render: (text: string) => {
        const { newItems, modifiedItems, deletedItems } = parseDiffData(text)

        // 如果没有任何变更
        if (
          newItems.length === 0 &&
          modifiedItems.length === 0 &&
          deletedItems.length === 0
        ) {
          return <span style={{ color: '#999' }}>-</span>
        }

        return (
          <div className="diff-content-display">
            {/* 新增项 */}
            {newItems.length > 0 &&
              newItems.map((item) => (
                <div key={item.item_id} className="item itemAdd">
                  <span className="itemLabel">新增</span>
                  <span className="itemContent">
                    {item.title}：{item.content}
                  </span>
                </div>
              ))}

            {/* 修改项 */}
            {modifiedItems.length > 0 &&
              modifiedItems.map((item) => (
                <div key={item.item_id} className="item itemModify">
                  <span className="itemLabel">修改</span>
                  <span className="itemContent">
                    {`${item.title}："${item.originalContent}"`}修改为"
                    {`${item.content}"`}
                  </span>
                </div>
              ))}

            {/* 删除项 */}
            {deletedItems.length > 0 &&
              deletedItems.map((item) => (
                <div key={item.item_id} className="item itemDelete">
                  <span className="itemLabel">删除</span>
                  <span className="itemContent">
                    {item.title}: {item.content}
                  </span>
                </div>
              ))}
          </div>
        )
      }
    },
    {
      title: '备注',
      dataIndex: 'remark',
      key: 'remark',
      width: 200,
      render: (text: string, record: TestDataItem) => (
        <TextArea
          autoSize={{ minRows: 3, maxRows: 6 }}
          value={text || ''}
          placeholder="请输入备注"
          onChange={(e) => {
            const value = e.target.value
            updateDataSource((prev) => {
              const updated = [...prev]
              const index = updated.findIndex((d) => d.key === record.key)
              if (index !== -1) {
                updated[index] = {
                  ...updated[index],
                  remark: value
                }
              }
              return updated
            })
          }}
          onFocus={() => {
            setHoveredRecord(record)
          }}
          onClick={() => {
            setHoveredRecord(record)
          }}
          style={{ width: '100%' }}
        />
      )
    }
  ]
}
